import { STANDARD_PARAMETER_KEYS } from "@repo/domain";
import { z } from "zod";

/** Raw FHIR row id. Not a domain identity — never stands in for one. */
export const resourceIdSchema = z.string().min(1);

/** A system-assigned member identity, `<prefix>-<n>`. Authors never type one; a form echoes it back. */
export const memberIdSchema = z.string().regex(/^[a-z][a-z0-9]*-[1-9]\d*$/);

/**
 * A unit of measure. `code` is a UCUM expression and carries the meaning;
 * `display` is what the clinician reads. No UCUM expression contains
 * whitespace, so a code with a space in it is a display string in the wrong
 * field and is rejected here rather than stored as a unit nothing matches.
 */
export const unitSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .refine((code) => !/\s/.test(code), { message: "A UCUM code must not contain whitespace" }),
  display: z.string().trim().min(1),
});

/** One option of a Choice value. Absent `id` means a new option the definition will mint. */
export const choiceOptionSchema = z.object({
  id: memberIdSchema.optional(),
  label: z.string().trim().min(1),
});

/**
 * What a parameter DECLARES it accepts — bounds, units, an option list — not a
 * recorded answer. Shared by every definition that carries parameters
 * (diagnostic, treatment, device kind), because the effective set a procedure
 * resolves reads those lists side by side and a divergence between two copies
 * of this union would make one of them unreadable against the other.
 *
 * The questionnaire union is deliberately NOT this one: it adds `Date`,
 * `Attachment` and a `clientRef` on options, which parameters have no use for.
 */
export const parameterValueSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("Quantity"),
    unit: unitSchema.optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    step: z.number().positive().optional(),
  }),
  z.object({
    kind: z.literal("Integer"),
    min: z.number().int().optional(),
    max: z.number().int().optional(),
    step: z.number().int().positive().optional(),
  }),
  z.object({
    kind: z.literal("Choice"),
    options: z.array(choiceOptionSchema).min(1),
    cardinality: z.enum(["Single", "Multiple"]),
    allowFreeText: z.boolean(),
  }),
  z.object({ kind: z.literal("Boolean") }),
  z.object({ kind: z.literal("Text"), maxLength: z.number().int().positive().optional() }),
]);

/**
 * Narrow FHIR reference input. Parses into the shape the domain's `FhirRef<T>`
 * expects: `{ resourceType, id }`. Refs carry identity only — no `display`,
 * no denormalized snapshot data. Name/display is resolved by the caller via
 * a follow-up query (tRPC handler, `_include`, TanStack DB join).
 */
export const fhirRefSchema = <T extends string>(resourceType: T) =>
  z.object({
    resourceType: z.literal(resourceType),
    id: resourceIdSchema,
  });

/**
 * Polymorphic FHIR reference input. Use when the target resourceType is one of
 * a known set (e.g. Task.owner can be Practitioner | Organization | CareTeam).
 * Callers must enumerate the allowed resourceTypes — unconstrained poly-ref
 * inputs are a hole, not a feature. Identity only — see `fhirRefSchema`.
 */
export const fhirRefPolySchema = <T extends string>(...resourceTypes: readonly [T, ...T[]]) =>
  z.object({
    resourceType: z.enum(resourceTypes as unknown as [T, ...T[]]),
    id: resourceIdSchema,
  });

/**
 * The allowed resourceTypes for an authenticated profile reference carried on
 * `TRPCContext.profile`. Routers that forward ctx.profile into a schema-typed
 * field (e.g. Task.requester, Task.author) use this to keep wire contracts
 * honest and avoid casts.
 */
export const profileResourceTypes = ["Practitioner", "Organization", "Patient", "RelatedPerson"] as const;
export type ProfileResourceType = (typeof profileResourceTypes)[number];

/** Profile ref carried on TRPCContext.profile. Identity only. */
export interface ProfileRef {
  resourceType: ProfileResourceType;
  id: string;
}

/** FHIR HumanName */
export const humanNameSchema = z.object({
  use: z.enum(["usual", "official", "temp", "nickname", "anonymous", "old", "maiden"]).optional(),
  family: z.string().optional(),
  given: z.array(z.string()).optional(),
  prefix: z.array(z.string()).optional(),
  suffix: z.array(z.string()).optional(),
});

/** FHIR ContactPoint */
export const contactPointSchema = z.object({
  system: z.enum(["phone", "fax", "email", "pager", "url", "sms", "other"]).optional(),
  value: z.string().optional(),
  use: z.enum(["home", "work", "temp", "old", "mobile"]).optional(),
});

/** FHIR Address */
export const addressSchema = z.object({
  use: z.enum(["home", "work", "temp", "old", "billing"]).optional(),
  type: z.enum(["postal", "physical", "both"]).optional(),
  line: z.array(z.string()).optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});

export const standardParameterReferenceSchema = z.object({
  kind: z.literal("standard"),
  key: z.enum(STANDARD_PARAMETER_KEYS),
});
